import { useCallback, useMemo } from 'react';
import { Product } from '../../../types/product.types';
import { formatCurrency } from '../../../utils/formatters/money';

export interface UseProductTableProps {
  products: Product[];
  selectedProducts: number[];
  setSelectedProducts: React.Dispatch<React.SetStateAction<number[]>>;
  onViewProduct: (id: number) => void;
  onEditProduct: (id: number) => void;
  onDuplicateProduct: (id: number) => void;
  onDeleteProduct: (id: number) => void;
}

export const useProductTable = ({
  products,
  selectedProducts,
  setSelectedProducts,
  onViewProduct,
  onEditProduct,
  onDuplicateProduct,
  onDeleteProduct,
}: UseProductTableProps) => {
  // Selection handlers
  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newSelected = products.map((product) => product.id);
        setSelectedProducts(newSelected);
      } else {
        setSelectedProducts([]);
      }
    },
    [products, setSelectedProducts],
  );

  const handleSelectOne = useCallback(
    (id: number) => {
      const selectedIndex = selectedProducts.indexOf(id);
      let newSelected: number[] = [];

      if (selectedIndex === -1) {
        newSelected = [...selectedProducts, id];
      } else {
        newSelected = selectedProducts.filter((productId) => productId !== id);
      }

      setSelectedProducts(newSelected);
    },
    [selectedProducts, setSelectedProducts],
  );

  const isSelected = useCallback(
    (id: number) => {
      return selectedProducts.indexOf(id) !== -1;
    },
    [selectedProducts],
  );

  // Status helpers
  const getStatusDisplay = useCallback((status: string) => {
    if (status === 'ACTIVE') return 'Activo';
    if (status === 'INACTIVE') return 'Inactivo';
    if (status === 'Activo' || status === 'Inactivo') return status;
    return status;
  }, []);

  const getStatusColor = useCallback(
    (status: string) => {
      const displayStatus = getStatusDisplay(status);
      return displayStatus === 'Activo' ? 'success' : 'error';
    },
    [getStatusDisplay],
  );

  // Price formatting
  const renderPrice = useCallback((price: unknown): string => {
    if (typeof price === 'number' && !isNaN(price)) {
      return formatCurrency(price);
    }
    if (typeof price === 'string' && price !== '') {
      const n = parseFloat(price);
      if (!isNaN(n)) return formatCurrency(n);
    }
    return '';
  }, []);

  // Selection state
  const isAllSelected = useMemo(() => {
    return products.length > 0 && selectedProducts.length === products.length;
  }, [products.length, selectedProducts.length]);

  const isIndeterminate = useMemo(() => {
    return selectedProducts.length > 0 && selectedProducts.length < products.length;
  }, [selectedProducts.length, products.length]);

  // Action handlers
  const handleViewProduct = useCallback(
    (id: number) => {
      onViewProduct(id);
    },
    [onViewProduct],
  );

  const handleEditProduct = useCallback(
    (id: number) => {
      onEditProduct(id);
    },
    [onEditProduct],
  );

  const handleDuplicateProduct = useCallback(
    (id: number) => {
      onDuplicateProduct(id);
    },
    [onDuplicateProduct],
  );

  const handleDeleteProduct = useCallback(
    (id: number) => {
      onDeleteProduct(id);
    },
    [onDeleteProduct],
  );

  return {
    // Selection
    handleSelectAll,
    handleSelectOne,
    isSelected,
    isAllSelected,
    isIndeterminate,

    // Status
    getStatusDisplay,
    getStatusColor,

    // Formatting
    renderPrice,

    // Actions
    handleViewProduct,
    handleEditProduct,
    handleDuplicateProduct,
    handleDeleteProduct,
  };
};
